import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Grid,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { temporadaService } from '../../../services/fashionService';
import { Temporada } from '../../../types/fashion';

const toInputDate = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const TemporadaForm: React.FC = () => {
  const { codigoTemporada } = useParams();
  const isEdit = Boolean(codigoTemporada);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Temporada>({
    codigoTemporada: '',
    nombreTemporada: '',
    fechaDesde: '',
    fechaHasta: '',
    estado: 'A',
  });

  useEffect(() => {
    if (!codigoTemporada) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await temporadaService.getTemporada(codigoTemporada);
        if (cancelled) return;
        setForm({
          ...data,
          fechaDesde: toInputDate(data.fechaDesde),
          fechaHasta: toInputDate(data.fechaHasta),
        });
      } catch {
        if (!cancelled) {
          enqueueSnackbar('No se pudo cargar la temporada', { variant: 'error' });
          navigate('/inventario/fashion/temporadas');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [codigoTemporada, enqueueSnackbar, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.codigoTemporada.trim() || !form.nombreTemporada.trim()) {
      enqueueSnackbar('Código y nombre son obligatorios', { variant: 'warning' });
      return;
    }
    const payload = {
      nombreTemporada: form.nombreTemporada.trim(),
      fechaDesde: form.fechaDesde || null,
      fechaHasta: form.fechaHasta || null,
      estado: form.estado,
    };
    try {
      setSaving(true);
      if (isEdit && codigoTemporada) {
        await temporadaService.updateTemporada(codigoTemporada, payload);
        enqueueSnackbar('Temporada actualizada', { variant: 'success' });
      } else {
        await temporadaService.createTemporada({
          codigoTemporada: form.codigoTemporada.trim(),
          ...payload,
        });
        enqueueSnackbar('Temporada creada', { variant: 'success' });
      }
      navigate('/inventario/fashion/temporadas');
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} component="form" onSubmit={handleSubmit}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/inventario/fashion/temporadas')}
                sx={{ textTransform: 'none' }}
              >
                Temporadas
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {isEdit ? 'Editar temporada' : 'Nueva temporada'}
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código"
                  fullWidth
                  required
                  disabled={isEdit}
                  value={form.codigoTemporada}
                  onChange={(e) => setForm((f) => ({ ...f, codigoTemporada: e.target.value.toUpperCase() }))}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nombre"
                  fullWidth
                  required
                  value={form.nombreTemporada}
                  onChange={(e) => setForm((f) => ({ ...f, nombreTemporada: e.target.value }))}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Fecha desde"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.fechaDesde || ''}
                  onChange={(e) => setForm((f) => ({ ...f, fechaDesde: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Fecha hasta"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.fechaHasta || ''}
                  onChange={(e) => setForm((f) => ({ ...f, fechaHasta: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.estado === 'A'}
                      onChange={(e) => setForm((f) => ({ ...f, estado: e.target.checked ? 'A' : 'I' }))}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate('/inventario/fashion/temporadas')}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={22} /> : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemporadaForm;
